import { isPlatformBrowser } from '@angular/common';
import { inject, Injectable, PLATFORM_ID } from '@angular/core';

import { environment } from '../../../environments/environment';

const DB_NAME = 'unlock-queue';
const STORE = 'reports';
const META = 'meta';
const BEARER_KEY = 'bearer';
const DB_VERSION = 1;
const SYNC_TAG = 'unlock-report-sync';
const FETCH_TIMEOUT_MS = 10_000;

interface QueuedReport {
  id: string; // event_id, also the IDB key
  url: string;
  // unlocked_at: epoch ms when the BLE unlock command completed
  body: { event_id: string; bluetooth_id: string; unlocked_at: number };
}

export interface ReportInput {
  tokenString: string;
  bluetoothId: string;
  unlockedAt: number; // epoch ms when the BLE unlock command completed
}

@Injectable({ providedIn: 'root' })
export class UnlockReportService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly isBrowser = isPlatformBrowser(this.platformId);

  private openDB(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      const req = indexedDB.open(DB_NAME, DB_VERSION);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(STORE)) {
          db.createObjectStore(STORE, { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains(META)) {
          db.createObjectStore(META);
        }
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error ?? new Error('open failed'));
    });
  }

  private async put(record: QueuedReport): Promise<void> {
    const db = await this.openDB();
    try {
      await new Promise<void>((resolve, reject) => {
        const tx = db.transaction(STORE, 'readwrite');
        tx.objectStore(STORE).put(record);
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error ?? new Error('put failed'));
      });
    } finally {
      db.close();
    }
  }

  private async all(): Promise<QueuedReport[]> {
    const db = await this.openDB();
    try {
      return await new Promise<QueuedReport[]>((resolve, reject) => {
        const tx = db.transaction(STORE, 'readonly');
        const req = tx.objectStore(STORE).getAll();
        req.onsuccess = () => resolve(req.result as QueuedReport[]);
        req.onerror = () => reject(req.error ?? new Error('getAll failed'));
      });
    } finally {
      db.close();
    }
  }

  private async remove(id: string): Promise<void> {
    const db = await this.openDB();
    try {
      await new Promise<void>((resolve, reject) => {
        const tx = db.transaction(STORE, 'readwrite');
        tx.objectStore(STORE).delete(id);
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error ?? new Error('delete failed'));
      });
    } finally {
      db.close();
    }
  }

  /**
   * Stores the current unlock-report bearer. Called on every successful claim,
   * so reconnect re-claims keep it fresh — the one bearer every queued report
   * (and the SW drain) uses at send time.
   */
  async setBearer(bearer: string): Promise<void> {
    if (!this.isBrowser) return;
    const db = await this.openDB();
    try {
      await new Promise<void>((resolve, reject) => {
        const tx = db.transaction(META, 'readwrite');
        tx.objectStore(META).put(bearer, BEARER_KEY);
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error ?? new Error('setBearer failed'));
      });
    } finally {
      db.close();
    }
  }

  private async getBearer(): Promise<string | null> {
    const db = await this.openDB();
    try {
      return await new Promise<string | null>((resolve, reject) => {
        const tx = db.transaction(META, 'readonly');
        const req = tx.objectStore(META).get(BEARER_KEY);
        req.onsuccess = () => resolve((req.result as string) ?? null);
        req.onerror = () => reject(req.error ?? new Error('getBearer failed'));
      });
    } finally {
      db.close();
    }
  }

  /** Enqueues an unlock report and asks the SW to flush it via Background Sync. */
  async report(input: ReportInput): Promise<void> {
    if (!this.isBrowser) return;
    // event_id must be "<token_string>|<uuid>" — the backend binds the dedup
    // namespace to the caller's token and rejects any other shape with 400.
    const eventId = `${input.tokenString}|${crypto.randomUUID()}`;
    const record: QueuedReport = {
      id: eventId,
      url: `${environment.apiUrl}/v1/used`,
      body: { event_id: eventId, bluetooth_id: input.bluetoothId, unlocked_at: input.unlockedAt },
    };
    await this.put(record);
    await this.requestSync();
  }

  /**
   * Registers a Background Sync if available; otherwise the queued item is
   * drained later by the app's init/online drain.
   */
  private async requestSync(): Promise<void> {
    try {
      const reg = await navigator.serviceWorker?.ready;
      const syncMgr = (reg as unknown as { sync?: { register(tag: string): Promise<void> } })?.sync;
      if (syncMgr) {
        await syncMgr.register(SYNC_TAG);
      }
    } catch {
      // SW not available — item stays queued for later drain
    }
  }

  /**
   * Posts every queued report with the current shared bearer. Removes on success
   * (2xx) or any non-retryable 4xx (e.g. 400 malformed, 403 dead token); keeps on
   * expired bearer (401 — refreshed by the next reconnect re-claim), rate limit
   * (429), network failure, or 5xx for a later drain.
   */
  async drain(): Promise<void> {
    if (!this.isBrowser) return;
    const bearer = await this.getBearer();
    if (!bearer) return; // nothing to authenticate with yet
    const records = await this.all();
    for (const r of records) {
      // AbortController + setTimeout rather than AbortSignal.timeout, which is
      // missing on older mobile browsers (pre-iOS 16) this PWA must still run on.
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
      try {
        const res = await fetch(r.url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${bearer}` },
          body: JSON.stringify(r.body),
          signal: controller.signal,
        });
        // Drop on success or any non-retryable 4xx (400 malformed, 403 revoked)
        // — a permanently-rejected report must not retry forever. 401 (expired
        // bearer) and 429 (rate limit) stay queued for the next drain.
        if (
          res.ok ||
          (res.status >= 400 && res.status < 500 && res.status !== 401 && res.status !== 429)
        ) {
          await this.remove(r.id);
        }
      } catch {
        // network error / timeout — keep for next drain
      } finally {
        clearTimeout(timeoutId);
      }
    }
  }
}
